import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { categories, events } from "@/mocks/events";
import useMockLoading from "@/hooks/useMockLoading";
import usePageMeta from "@/hooks/usePageMeta";
import EventCard from "@/components/feature/EventCard";
import { EventCardSkeleton } from "@/components/base/Skeleton";

const PAGE_SIZE = 6;
const TODAY = "2026-10-15";

const statusOptions = [
  { value: "all", label: "全部状态" },
  { value: "live", label: "进行中" },
  { value: "upcoming", label: "未开始" },
  { value: "finished", label: "已结束" },
];

const timeOptions = [
  { value: "all", label: "全部时间" },
  { value: "today", label: "今日" },
  { value: "future", label: "未来赛事" },
  { value: "past", label: "往期赛事" },
];

export default function EventExplorer() {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loading = useMockLoading(500);

  const activeCategory = categoryId ?? "all";
  const q = searchParams.get("q") ?? "";

  const [sub, setSub] = useState("all");
  const [status, setStatus] = useState("all");
  const [time, setTime] = useState("all");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setSub("all");
    setPage(1);
  }, [activeCategory]);

  useEffect(() => {
    setKeyword(q);
    setPage(1);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [sub, status, time]);

  const currentCategory = categories.find((c) => c.id === activeCategory);

  usePageMeta(
    currentCategory ? `${currentCategory.name}赛事 - 51每日大赛` : "赛事分类 - 51每日大赛",
    currentCategory
      ? `${currentCategory.desc} 在 51每日大赛 查看${currentCategory.name}项目的赛程安排与最新比赛结果。`
      : "按竞技项目浏览 51每日大赛 的全部赛事，涵盖篮球、足球、网球、电竞、赛车等多个分类的赛程与比赛结果。",
    currentCategory
      ? `${currentCategory.name},${currentCategory.name}赛事,赛程安排,比赛结果,51每日大赛`
      : "赛事分类,赛程安排,比赛结果,赛事资讯,51每日大赛",
  );

  const filtered = useMemo(() => {
    let list = events;
    if (activeCategory !== "all") list = list.filter((e) => e.categoryId === activeCategory);
    if (sub !== "all") list = list.filter((e) => e.subCategory === sub);
    if (status !== "all") list = list.filter((e) => e.status === status);
    if (time === "today") list = list.filter((e) => e.date === TODAY);
    if (time === "future") list = list.filter((e) => e.date > TODAY);
    if (time === "past") list = list.filter((e) => e.date < TODAY);
    if (q.trim()) {
      const key = q.trim().toLowerCase();
      list = list.filter(
        (e) =>
          e.title.toLowerCase().includes(key) ||
          e.home.name.toLowerCase().includes(key) ||
          e.away.name.toLowerCase().includes(key) ||
          e.categoryName.toLowerCase().includes(key) ||
          e.subCategory.toLowerCase().includes(key),
      );
    }
    return [...list].sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return a.date < b.date ? 1 : -1;
    });
  }, [activeCategory, sub, status, time, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setSub("all");
    setStatus("all");
    setTime("all");
    setPage(1);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const key = keyword.trim();
    if (key) navigate(`/events?q=${encodeURIComponent(key)}`);
    else navigate("/events");
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/events" className="hover:text-primary-600 transition-colors">赛事分类</Link>
            {currentCategory ? (
              <>
                <i className="ri-arrow-right-s-line" />
                <span className="text-foreground-800">{currentCategory.name}</span>
              </>
            ) : null}
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
            {currentCategory ? `${currentCategory.name}赛事` : "全部赛事"}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
            {currentCategory
              ? currentCategory.desc
              : "汇总本站覆盖的全部竞技项目赛事，支持按分类、状态与时间进行筛选，快速找到你关注的比赛。"}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Link
            to="/events"
            className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              activeCategory === "all"
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300"
            }`}
          >
            全部
          </Link>
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === c.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              <i className={c.icon} /> {c.name}
            </Link>
          ))}
        </div>

        {currentCategory ? (
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="text-xs text-foreground-500 mr-1">子分类：</span>
            <button
              type="button"
              onClick={() => setSub("all")}
              className={`px-3 py-1 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                sub === "all" ? "bg-secondary-900 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              全部
            </button>
            {currentCategory.subs.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSub(s)}
                className={`px-3 py-1 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  sub === s ? "bg-secondary-900 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
          <form onSubmit={submitSearch} className="relative w-full lg:max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索赛事 / 队伍 / 选手"
              className="w-full h-10 pl-9 pr-3 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400"
            />
          </form>

          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer"
          >
            <i className="ri-filter-3-line" /> 筛选
            {status !== "all" || time !== "all" ? (
              <span className="w-2 h-2 rounded-full bg-primary-500" />
            ) : null}
          </button>

          <div className={`${filterOpen ? "flex" : "hidden"} lg:flex flex-col sm:flex-row gap-3`}>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap">状态</span>
              <div className="flex flex-wrap gap-1">
                {statusOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setStatus(o.value)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      status === o.value ? "bg-primary-500 text-background-50" : "bg-background-50 border border-background-200 text-foreground-700"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap">时间</span>
              <div className="flex flex-wrap gap-1">
                {timeOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setTime(o.value)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      time === o.value ? "bg-primary-500 text-background-50" : "bg-background-50 border border-background-200 text-foreground-700"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-foreground-600">
            共找到 <strong className="text-foreground-950">{filtered.length}</strong> 场赛事
          </span>
          {q ? <span className="text-xs text-foreground-500">关键词：“{q}”</span> : null}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <EventCardSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-600">暂无符合条件的赛事，您可重置筛选</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 h-9 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {paged.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center justify-center gap-2 mt-10 flex-wrap">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${
                      page === i + 1 ? "bg-primary-500 text-background-50" : "border border-background-300 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  下一页
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}